/**
 * `billingAddress` — the traveler address the BFF applies on the *initial* checkout call
 * (hotels `/hotels/book-package-with-card`, condos `/condos/book`, properties `/property/book`,
 * cars `/cars/checkout`, activities `/activities/checkout`).
 *
 * It exists because the Stripe flows drop the legacy credit-card form, and that form was the only
 * place the address was collected. The BFF runs it through the same logic it used for the card's
 * address, so we send the very same values minus number, CVV and expiry.
 *
 * Rules from the BFF contract:
 *  - express only: it is read when `card` is null, and ignored entirely when a `card` is present;
 *  - initial call only — the express resume (`book-express-resume`) is a GET and cannot take it;
 *  - `addressLine` / `addressLine1` are interchangeable (the BFF prefers `addressLine1` and falls
 *    back to `addressLine`); sending both is harmless;
 *  - `country` / `countryCode`: the payment charge prefers `countryCode`, the booking record uses
 *    `country`, so send both when we have them.
 */
export interface IBillingAddressPayload {
  holderName?: string;
  addressLine?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
  phone?: string;
}

/** Any address-bearing checkout object (hotel/cars `card`, condo/weeks guest, activities holder). */
export interface IBillingAddressSource {
  holderName?: string | null;
  addressLine?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  stateCode?: string | null;
  country?: string | null;
  countryCode?: string | null;
  zipCode?: string | null;
  postalCode?: string | null;
  phone?: string | null;
}

const clean = (value: unknown): string | undefined => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.length ? text : undefined;
};

/**
 * Builds the `billingAddress` body field from whatever address the checkout already collected.
 * Returns `undefined` when there is nothing worth sending, so callers can spread it conditionally.
 */
export function buildBillingAddressPayload(
  source: IBillingAddressSource | null | undefined,
): IBillingAddressPayload | undefined {
  if (!source) {
    return undefined;
  }

  const addressLine = clean(source.addressLine) ?? clean(source.addressLine1);
  const country = clean(source.country);
  const countryCode = clean(source.countryCode) ?? country;

  const candidate: IBillingAddressPayload = {
    holderName: clean(source.holderName),
    addressLine,
    addressLine1: addressLine,
    addressLine2: clean(source.addressLine2),
    city: clean(source.city),
    state: clean(source.state) ?? clean(source.stateCode),
    country: country ?? countryCode,
    countryCode,
    zipCode: clean(source.zipCode) ?? clean(source.postalCode),
    phone: clean(source.phone),
  };

  const payload: IBillingAddressPayload = {};
  let hasValue = false;

  (Object.keys(candidate) as (keyof IBillingAddressPayload)[]).forEach((key) => {
    const value = candidate[key];
    if (value !== undefined) {
      payload[key] = value;
      hasValue = true;
    }
  });

  return hasValue ? payload : undefined;
}

const joinName = (givenName?: string | null, surname?: string | null): string =>
  [givenName, surname].filter(Boolean).join(' ');

/** Dashes are typed into the phone inputs but never sent — the engine payloads strip them too. */
const stripPhoneSeparators = (phone?: string | null): string | undefined =>
  typeof phone === 'string' ? phone.split('-').join('') : undefined;

/**
 * Cars express: the address is the one the guest typed into the billing-address form
 * (`carsReviewBookStore.stripe`), with the name and phone off the driver — the same values the card
 * carried before Stripe took the card over.
 */
export function buildCarsBillingAddressPayload(
  stripeInfo: IBillingAddressSource | null | undefined,
  driver: { givenName?: string | null; surname?: string | null; phone?: string | null } | null | undefined,
): IBillingAddressPayload | undefined {
  if (!stripeInfo && !driver) {
    return undefined;
  }

  return buildBillingAddressPayload({
    ...stripeInfo,
    holderName: joinName(driver?.givenName, driver?.surname),
    phone: stripeInfo?.phone || driver?.phone,
  });
}

/**
 * Condo / weeks express: the primary-address section is the only address on the page, and it is what
 * the card mirrored while a card was still being sent ("use my primary address"), so the guest is the
 * billing source as well as the traveler.
 */
export function buildGuestBillingAddressPayload(
  guest:
    | (IBillingAddressSource & { givenName?: string | null; surname?: string | null })
    | null
    | undefined,
  /**
   * The guest's own `countryCode` is the *supplier's* code — Interval's internal code, and an empty
   * string for RCI — while `country` holds the display name. The card resolved the real ISO code
   * (`getCountryIsoCode`) before sending, and the charge reads `countryCode`, so callers pass the
   * resolved ISO here rather than letting the supplier code through.
   */
  isoCountryCode?: string | null,
): IBillingAddressPayload | undefined {
  if (!guest) {
    return undefined;
  }

  const payload = buildBillingAddressPayload({
    ...guest,
    countryCode: isoCountryCode || undefined,
    holderName: joinName(guest.givenName, guest.surname),
    phone: stripPhoneSeparators(guest.phone),
  });

  if (payload && !isoCountryCode) {
    // Without an ISO code, leave `countryCode` out rather than taking the generic builder's
    // fallback to `country` — that fallback is right where `country` is itself an ISO code
    // (hotels, cars) but here it is a display name, which the charge must not receive.
    delete payload.countryCode;
  }

  return payload;
}
